import { memo, useCallback, useMemo } from 'react';

import { type MapInterface, MapView as MapViewContainer } from '@studio/map';

import { useMapViewDispatch } from '@src/contexts';
import { useActiveMapInitZoom, useActiveMapStyle } from '@src/hooks';
import { customFetch } from '@src/utils/CustomFetch';
import { getCachedBbox } from '@src/utils/storage';

import { MapViewActions } from './MapViewActions';

interface MapViewProps {
  children?: React.ReactNode;
}

export const MapView = memo(function MapView({ children }: MapViewProps) {
  const dispatch = useMapViewDispatch();

  const currentMapStyle = useActiveMapStyle();
  useActiveMapInitZoom();

  const initialBounds = useMemo(() => {
    const { data } = getCachedBbox();
    return data?.bbox;
  }, []);

  const handleAreaChange = useCallback(
    (area: number) => {
      dispatch({ area, hasArea: area > 0 });
    },
    [dispatch]
  );

  const handleReady = useCallback(
    (mapInstance: MapInterface) => {
      dispatch({ mapInstance });
    },
    [dispatch]
  );

  const handleDispose = useCallback(() => {
    dispatch({ mapInstance: null });
  }, [dispatch]);

  return (
    <MapViewContainer
      onAreaChange={handleAreaChange}
      onReady={handleReady}
      onDispose={handleDispose}
      enableDeleteKeys
      accessToken={import.meta.env.VITE_MAPBOX_TOKEN}
      // @ts-expect-error
      fetchTile={customFetch.fetchTile}
      initialBounds={initialBounds}
      initialStyle={currentMapStyle}
      // tabIndex can be overridden if you handle keys on the map div
      // tabIndex={0}
    >
      {children}
      <MapViewActions />
    </MapViewContainer>
  );
});

MapView.displayName = 'MapView';
